import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import {
  FieldValues,
  Control,
  Controller,
  useWatch,
  useFormState,
  UseFormSetValue,
} from 'react-hook-form';
import cx from 'clsx';
import { Input } from '@/components';
import { formatNetworth } from '@/utils';
import { CommonDependency } from '@/dependency';
import { CommonFields } from './CommonFields';
import style from './index.module.scss';

const max = 100;
const min = 1;

export const CashPrize = ({
  control,
  isL2Pay,
  turnOff,
  onSubmit,
  disabled,
  extraPaymentParams,
  setValue,
  hiddenEligibilityFields,
}: {
  control: Control<FieldValues, object>;
  isL2Pay: boolean;
  disabled: boolean;
  turnOff: () => void;
  extraPaymentParams?: Object;
  onSubmit: (data: any) => void;
  setValue: UseFormSetValue<any>;
  hiddenEligibilityFields?: string[];
}) => {
  const { t } = useTranslation();

  const { errors } = useFormState({ control });

  const [count, value] = useWatch({
    control,
    name: ['prize_count', 'prize_value'],
  });
  const PayModal = CommonDependency.components.PayModal;
  const _total = useMemo(() => count && value && count * value, [count, value]);
  const total = useMemo(() => formatNetworth(_total), [_total]);

  const txParams = useMemo(
    () =>
      _total
        ? {
            price: Math.round(+_total * 10000),
            message: `Pay $${_total} to create a Lucky Draw`,
            ...extraPaymentParams,
          }
        : undefined,
    [_total, extraPaymentParams],
  );

  const payTitle = useMemo(() => t(`LuckyDraw_CashPrize.payTitle`, { price: total }), [total, t]);

  const handlePaySubmit = useCallback(
    async (data: Object) => {
      await onSubmit(data);
    },
    [onSubmit],
  );

  return (
    <>
      <div className={style.prizeFormRow}>
        <div>
          <div className={style.title}>{t('LuckyDraw_CashPrize.1mxz8sm')}</div>
          {/* @ts-ignore */}
          <Controller
            name="prize_value"
            control={control}
            rules={{
              required: true,
              min: { value: min, message: t('LuckyDraw_CashPrize.10n74vw', { amount: min }) },
              pattern: {
                value: /^\d+$/,
                message: t('LuckyDraw_CashPrize.1jffb0s'),
              },
            }}
            render={({ field, fieldState }) => (
              <div className={style.prizeValue}>
                <span className={style.prizeDollar}>$</span>
                {/* @ts-ignore */}
                <Input
                  className={style.prizeValueInput}
                  type="number"
                  {...field}
                  disabled={disabled}
                />
              </div>
            )}
          />
          <div className={style.fieldDesc}>
            <div className={cx(style.fieldDesc, errors.prize_value?.message && style.isError)}>
              {errors.prize_value?.message || t('LuckyDraw_CashPrize.no9byi')}
            </div>
          </div>
        </div>
        <div className={style.prizeSymbol}>×</div>
        <div>
          <div className={style.title}>{t('LuckyDraw_CashPrize.110xvcp')}</div>
          {/* @ts-ignore */}
          <Controller
            name="prize_count"
            control={control}
            rules={{
              required: true,
              min: 1,
              max: { value: max, message: t('LuckyDraw_CashPrize.1smsh6', { amount: max }) },
              pattern: {
                value: /^\d+$/,
                message: t('LuckyDraw_CashPrize.1jffb0s'),
              },
            }}
            render={({ field, fieldState }) => (
              // @ts-ignore
              <Input
                className={style.prizeValue}
                type="number"
                {...field}
                maxLength={7}
                disabled={disabled}
              />
            )}
          />
          <div className={cx(style.fieldDesc, errors.prize_count?.message && style.isError)}>
            {errors.prize_count?.message || t('LuckyDraw_CashPrize.hlm5ab')}
          </div>
        </div>
        <div className={style.prizeSymbol}>=</div>
        <div>
          <div className={style.title}>{t('LuckyDraw_CashPrize.1xj79xm')}</div>
          <div className={cx(style.prizeValue, !total && style.isEmpty)}>{total || '$0.00'}</div>
          <div className={style.fieldDesc}>{CommonDependency.text?.payWithBalance}</div>
        </div>
      </div>
      <CommonFields
        control={control}
        disabled={disabled}
        setValue={setValue}
        hiddenEligibilityFields={hiddenEligibilityFields}
      />
      {isL2Pay ? (
        <PayModal
          turnOff={turnOff}
          txParams={txParams}
          payTitle={payTitle}
          onPaySubmit={handlePaySubmit}
        />
      ) : null}
    </>
  );
};
